<script>
import MediaUploader from "./MediaUploader.vue";
import MediaGrid from "./MediaGrid.vue";
import MediaDetailPanel from "./MediaDetailPanel.vue";
import mediaApi from "@/helpers/media/media-api.js";

export default {
  name: "MediaPickerModal",
  components: {
    MediaUploader,
    MediaGrid,
    MediaDetailPanel,
  },
  props: {
    modalId: {
      type: String,
      default: "ModalMediaPicker",
    },
    single: {
      type: Boolean,
      default: true,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    onlyImages: {
      type: Boolean,
      default: false,
    },
    type: {
      type: [String, Array],
      default: null,
    },
    extension: {
      type: [String, Array],
      default: null,
    },
    mimeType: {
      type: [String, Array],
      default: null,
    },
    maxFilesize: {
      type: Number,
      default: 10,
    },
    uploadOnly: {
      type: Boolean,
      default: false,
    },
    okButtonLabel: {
      type: String,
      default: "Seleccionar",
    },
  },
  data() {
    return {
      activeTab: 0,
      items: [],
      selectedItems: [],
      currentItem: null,
      loading: false,
      page: 1,
      perPage: 50,
      totalPages: 1,
      uploadUrl: process.env.VUE_APP_API_HOST + "/api/v1/media",
    };
  },
  computed: {
    selectedIds() {
      return this.selectedItems.map((item) => item.id);
    },
    listParams() {
      const params = {
        page: this.page,
        per_page: this.perPage,
        sort_by: "updated_at",
        sort: "desc",
      };
      if (this.onlyImages) params.only_images = 1;
      if (this.type) params.type = this.type;
      if (this.extension) params.extension = this.extension;
      if (this.mimeType) params.mime_type = this.mimeType;
      if (this.selectedIds.length) params.include_ids = this.selectedIds;
      return params;
    },
  },
  methods: {
    resetState() {
      this.activeTab = this.uploadOnly ? 0 : 0;
      this.page = 1;
      this.items = [];
      this.selectedItems = [...this.selected];
      this.currentItem = this.selectedItems[0] || null;
    },
    async loadMedia(append = false) {
      this.loading = true;
      try {
        const response = await mediaApi.list(this.listParams);
        const rows = response?.data?.data || [];
        const meta = response?.data?.meta || {};
        this.totalPages = meta.total_pages || 1;
        this.items = append ? [...this.items, ...rows] : rows;
      } catch (error) {
        console.error("Error loading media:", error);
      } finally {
        this.loading = false;
      }
    },
    onShown() {
      this.resetState();
      if (!this.uploadOnly) {
        this.loadMedia();
      }
    },
    onHidden() {
      this.$emit("cancel");
    },
    onToggle(item) {
      if (this.single) {
        this.selectedItems = [item];
      } else {
        const index = this.selectedItems.findIndex((i) => i.id === item.id);
        if (index >= 0) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems.push(item);
        }
      }
      this.currentItem = item;
    },
    onSelect(item) {
      this.currentItem = item;
    },
    onUploaded(media) {
      if (!media?.id) return;
      this.items.unshift(media);
      if (this.single) {
        this.selectedItems = [media];
      } else if (!this.selectedItems.find((i) => i.id === media.id)) {
        this.selectedItems.push(media);
      }
      this.currentItem = media;
      if (this.uploadOnly) {
        this.$emit("uploaded", media);
      }
    },
    onDetailUpdated(media) {
      const updateInList = (list) => {
        const idx = list.findIndex((i) => i.id === media.id);
        if (idx >= 0) this.$set(list, idx, media);
      };
      updateInList(this.items);
      updateInList(this.selectedItems);
      if (this.currentItem?.id === media.id) {
        this.currentItem = media;
      }
    },
    loadMore() {
      if (this.page < this.totalPages) {
        this.page += 1;
        this.loadMedia(true);
      }
    },
    confirmSelection() {
      if (this.uploadOnly) {
        this.$bvModal.hide(this.modalId);
        return;
      }
      const payload = this.single
        ? this.selectedItems[0] || null
        : [...this.selectedItems];
      this.$emit("confirm", payload);
      this.$bvModal.hide(this.modalId);
    },
  },
};
</script>

<template>
  <b-modal
    :id="modalId"
    size="xl"
    :title="uploadOnly ? 'Subir archivos' : 'Biblioteca de medios'"
    hide-footer
    @shown="onShown"
    @hidden="onHidden"
  >
    <b-tabs v-model="activeTab" v-if="!uploadOnly">
      <b-tab title="Subir archivos">
        <MediaUploader
          :upload-url="uploadUrl"
          :only-images="onlyImages"
          :type="type"
          :extension="extension"
          :mime-type="mimeType"
          :max-filesize="maxFilesize"
          @uploaded="onUploaded"
        />
      </b-tab>
      <b-tab title="Biblioteca de medios">
        <div class="row">
          <div class="col-md-9">
            <div v-if="loading && !items.length" class="text-center py-4 text-muted">
              Cargando...
            </div>
            <div v-else class="media-library-grid-wrapper">
              <MediaGrid
                :items="items"
                :selected-ids="selectedIds"
                :current-id="currentItem?.id"
                @toggle="onToggle"
              />
            </div>
            <div v-if="page < totalPages" class="text-center mt-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                :disabled="loading"
                @click="loadMore"
              >
                Mostrar más
              </button>
            </div>
          </div>
          <div class="col-md-3">
            <MediaDetailPanel
              :item="currentItem"
              @updated="onDetailUpdated"
            />
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <div v-else>
      <MediaUploader
        :upload-url="uploadUrl"
        :only-images="onlyImages"
        :type="type"
        :extension="extension"
        :mime-type="mimeType"
        :max-filesize="maxFilesize"
        @uploaded="onUploaded"
      />
    </div>

    <div class="d-flex justify-content-end mt-3 gap-2">
      <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide(modalId)">
        Cancelar
      </button>
      <button
        v-if="!uploadOnly"
        type="button"
        class="btn btn-primary"
        :disabled="!selectedItems.length"
        @click="confirmSelection"
      >
        {{ okButtonLabel }}
      </button>
    </div>
  </b-modal>
</template>
